import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom"; 
import { useSessionById } from "../../api/SessionsAPI";
import { useSessionResponses } from "../../api/SessionResponsesAPI";
import { useCompetenciesByIds } from "../../api/CompetenciesAPI";
import BreakdownWeb from '../../components/reports/BreakdownWeb';
import PhaseGridWeb from '../../components/reports/PhaseGridWeb';
import CinemaMapWeb from '../../components/reports/CinemaMapWeb';
import DistributionWeb from '../../components/reports/DistributionWeb';
import { PhaseDefs } from '../../constants/definitions';

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const styles = {
  page: {
    width: '210mm', // A4 width
    minHeight: '297mm', // A4 height
    margin: '0',
    padding: '20mm',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
    backgroundColor: 'white',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    marginBottom: '20px'
  },
  header: {
    marginBottom: '12px'
  },
  title: {
    fontSize: '20px',
    fontWeight: 900,
    marginBottom: '15px',
    color: '#333'
  },
  sessionDetailsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '15px'
  },
  leftDetails: {
    fontSize: '14px',
    color: '#666'
  },
  rightDetails: {
    fontSize: '14px',
    textAlign: 'right',
    color: '#666'
  },
  detailLine: {
    marginBottom: '4px'
  },
  divider: {
    borderBottom: '1px solid #e0e0e0',
    marginTop: '8px',
    marginBottom: '4px'
  },
  sectionHeader: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '12px',
    color: '#333'
  }
};

const PHASES = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];

// Map category names to chart keys
const categoryMap = {
  'skills': 'skills',
  'knowledge': 'knowledge',
  'mindset': 'mindset',
  'tool': 'instruments'
};

const ReportWeb = () => {
  // Get all possible parameter names
  const params = useParams();
  // Use either sessionId or id parameter, whichever is available
  const sessionId = params.sessionId || params.id;
  

  const { 
    data: session, 
    isLoading: sessionLoading, 
    error: sessionError 
  } = useSessionById(sessionId);
  
  const { 
    data: sessionResponses, 
    isLoading: responsesLoading 
  } = useSessionResponses(sessionId);

  // Get competency IDs from session responses
  const competencyIds = sessionResponses?.map(response => response.competency_id) || [];
  
  // Fetch competency details using the IDs
  const { 
    data: competencies, 
    isLoading: competenciesLoading 
  } = useCompetenciesByIds(competencyIds);

  const [breakdownData, setBreakdownData] = useState(null);

  // Log data when it changes
  useEffect(() => {
    if (sessionResponses) {
      console.log("Session Responses:", sessionResponses);
      console.log("Extracted Competency IDs:", competencyIds);
    }
  }, [sessionResponses, competencyIds]);

  useEffect(() => {
    if (competencies) {
      console.log("Fetched Competencies:", competencies);
      
      // Group competencies by category and phase
      const groupedByCategoryAndPhase = competencies.reduce((acc, comp) => {
        const category = comp.category === 'tool' ? 'instruments' : (comp.category || 'uncategorized');
        const phase = comp.abilities?.phase || 'uncategorized';
        
        if (!acc[category]) {
          acc[category] = {
            phases: {
              Empathize: 0,
              Define: 0,
              Ideate: 0,
              Prototype: 0,
              Test: 0,
              Communicate: 0
            },
            total: 0
          };
        }
        
        acc[category].phases[phase]++;
        acc[category].total++;
        
        return acc;
      }, {});

      // Calculate percentages for breakdown - back to category-based with phase segments
      const categoryPhasePercentages = {};
      
      Object.entries(groupedByCategoryAndPhase).forEach(([category, data]) => {
        categoryPhasePercentages[category] = {
          total: data.total,
          Empathize: Math.round((data.phases.Empathize / data.total) * 100) || 0,
          Define: Math.round((data.phases.Define / data.total) * 100) || 0,
          Ideate: Math.round((data.phases.Ideate / data.total) * 100) || 0,
          Prototype: Math.round((data.phases.Prototype / data.total) * 100) || 0,
          Test: Math.round((data.phases.Test / data.total) * 100) || 0,
          Communicate: Math.round((data.phases.Communicate / data.total) * 100) || 0
        };
      });

      // Add console.log to debug the data
      console.log("Grouped Data:", groupedByCategoryAndPhase);
      console.log("Breakdown Data:", categoryPhasePercentages);

      setBreakdownData(categoryPhasePercentages);
    }
  }, [competencies]);

  // Group competencies by phase and then by category within each ability
  const groupedByPhase = React.useMemo(() => {
    if (!competencies) return {};

    return PHASES.reduce((acc, phase) => {
      // Initialize each phase
      acc[phase] = {
        abilities: {}
      };

      // Filter competencies for this phase
      const phaseCompetencies = competencies.filter(comp => 
        comp.abilities?.phase === phase
      );

      // Group by ability
      phaseCompetencies.forEach(comp => {
        const abilityName = comp.abilities?.name || 'Uncategorized';
        
        // Initialize the ability object if it doesn't exist
        if (!acc[phase].abilities[abilityName]) {
          acc[phase].abilities[abilityName] = {
            skills: [],
            knowledge: [],
            mindset: [],
            tool: []
          };
        }

        // Add competency to appropriate category
        if (comp.category) {
          // Make sure the array exists
          if (!acc[phase].abilities[abilityName][comp.category]) {
            acc[phase].abilities[abilityName][comp.category] = [];
          }
          acc[phase].abilities[abilityName][comp.category].push(comp.name);
        }
      });

      return acc;
    }, {});
  }, [competencies]);

  // Add console.log to debug
  useEffect(() => {
    if (groupedByPhase) {
      console.log("Grouped by Phase:", groupedByPhase);
    }
  }, [groupedByPhase]);

  // Removed radar chart readiness logic

  if (sessionLoading || responsesLoading || competenciesLoading) {
    return (
      <div className="h-full p-6">
        <div className="flex items-center justify-center h-full">
          Loading report data...
        </div>
      </div>
    );
  }

  if (sessionError) {
    return <div className="p-6">Error loading session: {sessionError.message}</div>;
  }

  return (
    <div className="h-full p-6 flex flex-col gap-6 overflow-y-auto bg-gray-100">
     
      <div className="w-full min-h-0 flex-1">
        <div className="space-y-6">
          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.title}>Competency Mapping Report</p>
              
              <div style={styles.sessionDetailsContainer}>
                <div style={styles.leftDetails}>
                  <p style={styles.detailLine}>Session ID: {session?.id}</p>
                  <p style={styles.detailLine}>Started At: {formatDate(session?.created_at || session?.createdAt)}</p>
                  {session?.cohort_id && (
                    <p style={styles.detailLine}>Cohort ID: {session.cohort_id}</p>
                  )}
                </div>
                
                <div style={styles.rightDetails}>
                  <p style={styles.detailLine}>Total Competencies: {competencies?.length || 0}</p>
                  <p style={styles.detailLine}>Status: {session?.status || "in_progress"}</p>
                </div>
              </div>
              
              <div style={styles.divider} />
            </div>
            
            <div>
              <p style={styles.sectionHeader}>The Big Picture</p>
              <CinemaMapWeb sessionResponses={sessionResponses} />
            </div>
          </div>

          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.sectionHeader}>The Breakdown</p>
              <div style={styles.divider} />
            </div>
            
            <BreakdownWeb data={breakdownData} />
          </div>

          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.sectionHeader}>The Distribution</p>
              <div style={styles.divider} />
            </div>
            
            <DistributionWeb data={breakdownData} />
          </div>

          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.sectionHeader}>The Details</p>
              <div style={styles.divider} />
            </div>
            
            {PHASES.map(phase => (
              <PhaseGridWeb 
                key={phase}
                phase={phase}
                description={PhaseDefs[phase.toUpperCase()]}
                data={groupedByPhase[phase]?.abilities || {}}
              />
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default ReportWeb;